import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/shared/components';

interface Props {
  rooms: string[];
  onChange: (rooms: string[]) => void;
  /** Mit Fotos heißt jeder Raum „Foto verlangt“, ohne bleibt er nur gespeichert. */
  photos: boolean;
}

const ICON =
  'text-ink-soft hover:text-ink rounded-pill grid size-11 place-items-center disabled:opacity-30';

/** Raumliste in der Reihenfolge, in der die Räume in der Aufgabe erscheinen. */
export function RoomListEditor({ rooms, onChange, photos }: Props) {
  const setAt = (idx: number, text: string) =>
    onChange(rooms.map((r, i) => (i === idx ? text : r)));
  const remove = (idx: number) => onChange(rooms.filter((_, i) => i !== idx));
  const move = (idx: number, delta: number) => {
    const target = idx + delta;
    if (target < 0 || target >= rooms.length) return;
    const next = [...rooms];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <ol className="border-hairline rounded-tile divide-hairline divide-y border bg-white">
        {rooms.map((room, idx) => (
          <li key={idx} className="flex items-center gap-1 py-1 pl-3 pr-1">
            <span className="text-ink-faint w-5 text-center font-mono text-xs tabular-nums">
              {idx + 1}
            </span>
            <input
              type="text"
              value={room}
              aria-label={`Raum ${idx + 1}`}
              maxLength={500}
              placeholder="Raum, z. B. Bad"
              onChange={(e) => setAt(idx, e.target.value)}
              className="rounded-control focus-visible:outline-forest min-h-11 min-w-0 flex-1 bg-transparent px-2 text-sm"
            />
            <span className="text-ink-soft hidden text-xs sm:inline">
              {photos ? 'Foto verlangt' : 'kein Foto'}
            </span>
            <button
              type="button"
              onClick={() => move(idx, -1)}
              disabled={idx === 0}
              className={ICON}
              aria-label={`${room || `Raum ${idx + 1}`} nach oben`}
            >
              <ChevronUp size={16} />
            </button>
            <button
              type="button"
              onClick={() => move(idx, 1)}
              disabled={idx === rooms.length - 1}
              className={ICON}
              aria-label={`${room || `Raum ${idx + 1}`} nach unten`}
            >
              <ChevronDown size={16} />
            </button>
            <button
              type="button"
              onClick={() => remove(idx)}
              className={`${ICON} hover:text-sealwax`}
              aria-label={`${room || `Raum ${idx + 1}`} entfernen`}
            >
              <Trash2 size={16} />
            </button>
          </li>
        ))}
      </ol>
      <Button
        variant="secondary"
        className="min-h-11 self-start"
        leftIcon={<Plus size={16} aria-hidden />}
        onClick={() => onChange([...rooms, ''])}
      >
        Raum hinzufügen
      </Button>
    </div>
  );
}
